import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Tabs from '../components/tabs/Tabs';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { getGuaranteeLoanDetail, getGuaranteeCheckDetail, getGuaranteePromissoryNoteDetail, getGuaranteeCommercialEndorsementDetail } from '../services/GuaranteesService';
import GeneralTab from '../organisms/MonetizationDetail/GeneralTab';
import InstallmentPlanTab from '../organisms/MonetizationDetail/InstallmentPlanTab';
import MonetizationWeighingTab from '../organisms/MonetizationDetail/MonetizationWeighingTab';
import CommissionsTab from '../organisms/MonetizationDetail/CommissionsTab';
import SindicatedTab from '../organisms/MonetizationDetail/SindicatedTab';
import GuaranteeAttachmentsTab from '../organisms/MonetizationDetail/GuaranteeAttachmentsTab';
import { Toast } from '../components/toast/toast';
import PageBreadcrumb from '../components/PageBreadcrumb/PageBreadcrumb';
import { routes } from '../routes/routePaths';

interface MonetizationDetailData {
  id: string;
  id_global: string;
  company_name: string;
  cuit: string;
  operation_type: string;
  guarantee_type: string;
  created_at: string;
  monetization_amount: number;
  net_monetization_amount: number;
  currency: string;
  issue_date: string;
  due_date: string;
  creditor: string;
  status_type: string;
  referrer_name: string;
  application_product_line_id: number;
  [key: string]: any;
}

const MonetizationDetail = () => {
  const { cuit, lineId, id } = useParams<{ cuit: string; lineId: string; id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [detailData, setDetailData] = useState<MonetizationDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guaranteeType, setGuaranteeType] = useState<'loan' | 'check' | 'promissory_note' | 'commercial_endorsement' | null>(null);
  const [installmentRefreshKey, setInstallmentRefreshKey] = useState(0);
  const [weighingRefreshKey, setWeighingRefreshKey] = useState(0);

  // Determinar el tipo de garantía desde la URL
  const getGuaranteeTypeFromPath = (): 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement' | null => {
    const pathname = location.pathname;
    if (pathname.includes('/prestamo/')) return 'loan';
    if (pathname.includes('/cheque/')) return 'check';
    if (pathname.includes('/pagare/')) return 'promissory_note';
    if (pathname.includes('/aval-comercial/')) return 'commercial_endorsement';
    return null;
  };

  useEffect(() => {
    if (id) {
      fetchMonetizationDetail();
    }
  }, [id]);

  const fetchMonetizationDetail = async (silent = false) => {
    if (!id) return;

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      // Determinar el tipo de garantía desde la URL primero
      const urlGuaranteeType = getGuaranteeTypeFromPath();
      
      let response;
      
      if (urlGuaranteeType) {
        // Si tenemos el tipo desde la URL, llamar directamente al endpoint correcto
        if (urlGuaranteeType === 'loan') {
          response = await getGuaranteeLoanDetail(id);
        } else if (urlGuaranteeType === 'check') {
          response = await getGuaranteeCheckDetail(id);
        } else if (urlGuaranteeType === 'promissory_note') {
          response = await getGuaranteePromissoryNoteDetail(id);
        } else if (urlGuaranteeType === 'commercial_endorsement') {
          response = await getGuaranteeCommercialEndorsementDetail(id);
        }
        
        if (response.success && 'data' in response) {
          setDetailData(response.data);
          setGuaranteeType(urlGuaranteeType);
          if (!silent) {
            setLoading(false);
          }
          return;
        }
      }

      if (!silent) {
        setError('Error al cargar los datos de la monetización');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching monetization detail:', err);
      if (!silent) {
        setError('Error al cargar los datos de la monetización');
        setLoading(false);
      }
    }
  };

  const handleGoBack = () => {
    // Navegar a la vista anterior, si existe en el state
    if (location.state?.from) {
      navigate(location.state.from);
    } else if (cuit && lineId) {
      navigate(`/avales/${cuit}/linea/${lineId}`);
    } else {
      navigate(-1);
    }
  };

  const avalBreadcrumbLabel = `Aval ${detailData?.id_global ?? id ?? ''}`;
  const breadcrumb = (
    <PageBreadcrumb
      items={[
        { label: 'Inicio', to: routes.dashboard },
        { label: 'Monetización', to: routes.monetization },
        { label: avalBreadcrumbLabel },
      ]}
    />
  );

  if (loading) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
        <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
          <Card margin={{ top: '4' }}>
            <TableSkeleton rows={8} columns={4} />
          </Card>
        </Card>
      </>
    );
  }

  if (error || !detailData || !guaranteeType) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
        <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
          <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
            <P type="1" weight="regular" modifier="weak">{error || 'Monetización no encontrada'}</P>
          </Card>
        </Card>
      </>
    );
  }

  // Determinar qué tabs mostrar según el tipo de garantía
  const tabs = [
    {
      label: 'General',
      content: <GeneralTab data={detailData} guaranteeType={guaranteeType} installmentRefreshKey={installmentRefreshKey} onDataUpdate={() => { fetchMonetizationDetail(); setInstallmentRefreshKey(k => k + 1); setWeighingRefreshKey(k => k + 1); }} />
    }
  ];

  // Agregar "Plan de cuotas" solo para préstamos
  if (guaranteeType === 'loan') {
    tabs.push({
      label: 'Plan de cuotas',
      content: <InstallmentPlanTab loanId={id!} loanData={detailData} refreshKey={installmentRefreshKey} onPlanReplaced={() => setInstallmentRefreshKey(k => k + 1)} />
    });
  }

  // Agregar tabs comunes
  tabs.push(
    {
      label: 'Monetizacion y Ponderacion',
      content: (
        <MonetizationWeighingTab
          guaranteeId={id!}
          guaranteeType={guaranteeType}
          refreshKey={weighingRefreshKey}
          onWeighingUpdated={() => {
            fetchMonetizationDetail(true);
          }}
        />
      )
    },
    {
      label: 'Comisiones',
      content: <CommissionsTab guaranteeId={id!} guaranteeType={guaranteeType} idGlobal={detailData.id_global} montoAval={detailData.monetization_amount} currency={detailData.currency} />
    }
  );

  // Agregar "Adjuntos de la Garantia" solo para préstamos
  if (guaranteeType === 'loan') {
    tabs.push({
      label: 'Adjuntos de la Garantia',
      content: <GuaranteeAttachmentsTab idGlobal={detailData.id_global} />
    });
  }

  // Agregar tab final común
  tabs.push({
    label: 'Operaciones Sindicadas',
    content: <SindicatedTab guaranteeId={id!} guaranteeType={guaranteeType} />
  });

  const lineContext = (location.state as any)?.lineContext as {
    approvedAmount: number;
    totalUsedAmount: number;
    availableAmount: number;
    productLineLabel: string;
  } | undefined;

  return (
    <>
      <div style={{ width: '85vw' }}>{breadcrumb}</div>
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      {/* Header with back button and titles */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a línea de producto"
        />
        <Card display='flex' alignItems='baseline' gap='8px'>
            <H withMargin={false} variant="secondary-2" type='2' weight='bold' modifier='strong'>
            Aval Nº {detailData.id_global}
            </H>
            <H variant="secondary-2" type="3" weight="bold" modifier="strong">
            {detailData.company_name}
            </H>
        </Card>
      </Card>

      {/* Resumen de la línea — pasado como contexto desde ProductLineDetail */}
      {lineContext && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <Card variant='neutral' modifier='regular' spacing='3'>
            <P type='3' variant='neutral' weight='bold' withMargin={false}>Total aprobado</P>
            <H withMargin={false} variant='secondary-2' type='3' weight='bold' modifier='strong'>
              {lineContext.approvedAmount.toLocaleString('es-AR')}
            </H>
          </Card>
          <Card variant='neutral' modifier='regular' spacing='3'>
            <P type='3' variant='neutral' weight='bold' withMargin={false}>Utilizado</P>
            <H withMargin={false} variant='secondary-2' type='3' weight='bold' modifier='strong'>
              {lineContext.totalUsedAmount.toLocaleString('es-AR')}
            </H>
          </Card>
          <Card variant='neutral' modifier='regular' spacing='3'>
            <P type='3' variant='neutral' weight='bold' withMargin={false}>Disponible</P>
            <H withMargin={false} variant='secondary-2' type='3' weight='bold' modifier='strong'>
              {lineContext.availableAmount.toLocaleString('es-AR')}
            </H>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Card margin={{ top: '4' }}>
        <Tabs tabs={tabs} defaultActiveTab={0} />
      </Card>
      <Toast id='early-cancellation-toast' />
      <Toast id='reinsurance-toast' />
      </Card>
    </>
  );
};

export default MonetizationDetail;
